import { BarDatum, ResponsiveBar } from "@nivo/bar";
import {
  ChartBlock,
  ChartTitle,
  ChartWrapper,
  CustomTooltip,
  CustomTooltipY,
  CustomTooltipYLabel,
  CustomTooltipYVal,
} from "../Styles/Chart.styled";
import { BaseChartProps } from "../../common/chart-config";
import { NoResult } from "../Common/NoResult";
import { mergeObjects } from "./helper";
import moment from "moment";
import Loader from "../Common/Loader";

interface ChartProps {
  title?: string;
  data: BarDatum[];
  myAveragePriceData?: BarDatum[];
  providerName?: string;
  compareAveragePriceData?: BarDatum[];
  compareProviderName?: string;
  isLoading?: boolean;
}

export const PriceBarChart: React.FC<ChartProps> = ({
  title,
  data,
  myAveragePriceData = [],
  providerName,
  compareAveragePriceData = [],
  compareProviderName,
  isLoading = false,
}) => {
  let isMobile = document.body.clientWidth <= 786;

  const dataSize = data?.length || 0;
  const hasMyData = myAveragePriceData && myAveragePriceData.length > 0;
  const hasCompareData = compareAveragePriceData && compareAveragePriceData.length > 0 && compareProviderName;

  const getTickValues = (data: any, interval = 3) => {
    return data
      .filter((_: any, index: any) => index % interval === 0)
      .map((d: any) => d.date);
  };

  const combinedData = data.map((item: any) => {
    const myData = myAveragePriceData.find((myItem: any) => myItem.date === item.date);
    const compareData = hasCompareData
      ? compareAveragePriceData.find((c: any) => c.date === item.date)
      : null;
    return {
      date: item.date,
      "Market Average": item.value,
      ...(providerName && { [providerName]: myData ? myData.value : null }),
      ...(hasCompareData && compareProviderName && { [compareProviderName]: compareData ? compareData.value : null }),
    };
  });

  const chartKeys = hasCompareData
    ? ["Market Average", providerName, compareProviderName].filter(Boolean)
    : hasMyData
    ? ["Market Average", providerName]
    : ["Market Average"];
  const chartColors = hasCompareData
    ? ["rgb(77, 175, 74)", "rgb(55, 126, 184)", "rgb(214, 39, 40)"]
    : hasMyData
    ? ["rgb(77, 175, 74)", "rgb(55, 126, 184)"]
    : ["rgb(77, 175, 74)"];

  const chatConfig = {
    keys: chartKeys,
    indexBy: "date",
    margin: { bottom: (hasMyData || hasCompareData) ? 100 : 50 },
    padding: (hasMyData || hasCompareData) ? 0.3 : 0.7,
    groupMode: (hasMyData || hasCompareData) ? "grouped" : "stacked",
    valueScale: { type: "linear" },
    indexScale: { type: "band", round: true },
    colors: chartColors,
    axisBottom: {
      tickValues: isMobile ? 1 : getTickValues(data, dataSize > 12 ? 2 : 1),
      format: (value: string) =>
        moment(value, "YYYY-MM-DD").format("MMM, YYYY"),
    },
    axisLeft: {
      legend: "Rate/kWh (in cents)",
    },
    enableLabel: false,
    fill: [],
    legends: (hasMyData || hasCompareData) ? [{
        anchor: 'bottom',
        direction: 'row',
        justify: false,
        translateY: 80,
        itemsSpacing: 8,
        itemDirection: 'top-to-bottom',
        symbolShape: 'circle',
        itemWidth: 200,
        itemHeight: 20,
        symbolSize: 14,
    }] : [],
    tooltip: (point: any) => {
      if (point) {
        const { data, color, id } = point || {};
        if (data && data.date) {
          const xValFormatted = data?.date
            ? moment(data?.date, "YYYY-MM-DD").format("MMM, YYYY")
            : "";
          const yValue = data[id];
          if (yValue !== null && yValue !== undefined) {
            return (
              <CustomTooltip>
                <CustomTooltipY>
                  <CustomTooltipYLabel color={color}>
                    {id}: {xValFormatted}
                  </CustomTooltipYLabel>
                  <CustomTooltipYVal>{`${yValue}¢`}</CustomTooltipYVal>
                </CustomTooltipY>
              </CustomTooltip>
            );
          }
        }
      }
      return null;
    },
  };

  const newConfig = mergeObjects(BaseChartProps, chatConfig);

  return (
    <ChartBlock style={{ minHeight: 300 }}>
      {title && <ChartTitle>{title}</ChartTitle>}
      <ChartWrapper xs>
        {isLoading ? (
          <Loader />
        ) : data?.length === 0 ? (
          <NoResult />
        ) : (
          <ResponsiveBar {...newConfig} data={combinedData} />
        )}
      </ChartWrapper>
    </ChartBlock>
  );
};
